import { CalendarDays, Clock3, Code2, ExternalLink, RefreshCw, Trophy, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { api, type MemberFilter, type PublicRankingPeriod, type PublicRankingResponse, type SimplePublicRankingPeriod } from "../api/client";
import { MemberFilter as MemberFilterControl } from "../components/MemberFilter";

const numberFormat = new Intl.NumberFormat("zh-HK");
const dateTimeFormat = new Intl.DateTimeFormat("zh-HK", {
  timeZone: "Asia/Taipei",
  dateStyle: "medium",
  timeStyle: "short"
});

const periods: Array<{ value: PublicRankingPeriod; label: string }> = [
  { value: "today", label: "今日" },
  { value: "week", label: "本週(近7天)" },
  { value: "month", label: "本月(近30天)" }
];
const simpleApiPeriods: Array<{ value: SimplePublicRankingPeriod; label: string }> = [
  { value: "day", label: "rank_of_day" },
  { value: "week", label: "rank_of_week" },
  { value: "month", label: "rank_of_month" }
];
const PUBLIC_RANKING_LIMIT = 10;

function formatGeneratedAt(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateTimeFormat.format(date);
}

function rankingUrl(period: PublicRankingPeriod, member: MemberFilter, limit: number): string {
  return `/_view-counter/rankings/${period}?member=${encodeURIComponent(member)}&limit=${encodeURIComponent(
    String(limit)
  )}`;
}

function simpleRankingUrl(period: SimplePublicRankingPeriod): string {
  return `/_view-counter/rank_of_${period}`;
}

export function PublicRankingsPage() {
  const [period, setPeriod] = useState<PublicRankingPeriod>("today");
  const [member, setMember] = useState<MemberFilter>("all");
  const [ranking, setRanking] = useState<PublicRankingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const endpoint = useMemo(() => rankingUrl(period, member, PUBLIC_RANKING_LIMIT), [period, member]);
  const simpleEndpoint = period === "today" ? simpleRankingUrl("day") : simpleRankingUrl(period);
  const items = ranking?.items ?? [];
  const leaders = items.slice(0, 3);
  const summary = useMemo(() => {
    return {
      views: items.reduce((sum, item) => sum + item.views, 0),
      visitors: items.reduce((sum, item) => sum + item.uniqueVisitors, 0),
      memberArticles: items.filter((item) => item.isMemberOnly).length,
      publicArticles: items.filter((item) => !item.isMemberOnly).length
    };
  }, [items]);

  async function loadRanking() {
    setLoading(true);
    setError("");
    try {
      setRanking(await api.publicRanking(period, { member, limit: PUBLIC_RANKING_LIMIT }));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : String(loadError));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadRanking();
  }, [period, member]);

  return (
    <div className="page-stack">
      <div className="toolbar-row">
        <h2>公開排行</h2>
        <div className="icon-actions">
          <a className="icon-link" href={endpoint} target="_blank" rel="noreferrer" title="開啟公開 JSON">
            <ExternalLink size={16} aria-hidden="true" />
          </a>
          <button type="button" onClick={loadRanking} disabled={loading} title="重新整理">
            <RefreshCw size={16} aria-hidden="true" />
          </button>
        </div>
      </div>

      <section className="panel ranking-controls-panel">
        <div className="ranking-controls">
          <div className="segmented" role="tablist" aria-label="排行期間">
            {periods.map((item) => (
              <button
                key={item.value}
                type="button"
                className={period === item.value ? "active" : ""}
                onClick={() => setPeriod(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <MemberFilterControl value={member} onChange={setMember} />
        </div>
        <div className="ranking-api-grid">
          <a className="ranking-api-card active" href={endpoint} target="_blank" rel="noreferrer">
            <Code2 size={18} aria-hidden="true" />
            <span>完整 JSON</span>
            <strong>{endpoint}</strong>
          </a>
          {simpleApiPeriods.map((item) => {
            const url = simpleRankingUrl(item.value);
            return (
              <a
                key={item.value}
                className={url === simpleEndpoint ? "ranking-api-card active-simple" : "ranking-api-card"}
                href={url}
                target="_blank"
                rel="noreferrer"
              >
                <Code2 size={18} aria-hidden="true" />
                <span>{item.label}</span>
                <strong>{url}</strong>
              </a>
            );
          })}
        </div>
      </section>

      {error ? <div className="error-state">{error}</div> : null}

      <section className="ranking-meta-grid">
        <article className="status-card">
          <CalendarDays size={18} aria-hidden="true" />
          <div>
            <strong>
              {ranking?.from ?? "-"} 至 {ranking?.to ?? "-"}
            </strong>
            <span>{ranking?.timeZone ?? "Asia/Taipei"}</span>
          </div>
        </article>
        <article className="status-card">
          <Clock3 size={18} aria-hidden="true" />
          <div>
            <strong>{ranking ? formatGeneratedAt(ranking.generatedAt) : "-"}</strong>
            <span>{loading ? "更新中" : "generatedAt"}</span>
          </div>
        </article>
        <article className="status-card">
          <Trophy size={18} aria-hidden="true" />
          <div>
            <strong>{numberFormat.format(summary.views)}</strong>
            <span>Top {items.length || PUBLIC_RANKING_LIMIT} 觀看</span>
          </div>
        </article>
        <article className="status-card">
          <Users size={18} aria-hidden="true" />
          <div>
            <strong>{numberFormat.format(summary.visitors)}</strong>
            <span>
              會員 {summary.memberArticles} · 一般 {summary.publicArticles}
            </span>
          </div>
        </article>
      </section>

      {leaders.length > 0 ? (
        <section className="ranking-leader-grid">
          {leaders.map((item) => (
            <article key={`leader-${item.articleId}`} className={`ranking-leader-card rank-${item.rank}`}>
              <span className="leader-rank">#{item.rank}</span>
              <a href={item.articleUrl || item.canonicalUrl} target="_blank" rel="noreferrer">
                {item.title}
                <ExternalLink size={13} aria-hidden="true" />
              </a>
              <div>
                <span className={item.isMemberOnly ? "badge member" : "badge public"}>{item.isMemberOnly ? "會員" : "一般"}</span>
                <strong>{numberFormat.format(item.views)}</strong>
              </div>
            </article>
          ))}
        </section>
      ) : null}

      <section className="panel">
        {ranking && items.length > 0 ? (
          <div className="table-wrap">
            <table className="ranking-table">
              <thead>
                <tr>
                  <th>排名</th>
                  <th>文章</th>
                  <th>類型</th>
                  <th>發布日</th>
                  <th>觀看</th>
                  <th>訪客</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={`${ranking.period}-${item.articleId}-${item.rank}`}>
                    <td>
                      <span className={item.rank <= 3 ? "rank-number rank-number-top" : "rank-number"}>{item.rank}</span>
                    </td>
                    <td>
                      <div className="article-cell">
                        <a href={item.articleUrl || item.canonicalUrl} target="_blank" rel="noreferrer">
                          {item.title}
                          <ExternalLink size={13} aria-hidden="true" />
                        </a>
                        <span>
                          {item.articleId} · {item.author || "Unknown"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={item.isMemberOnly ? "badge member" : "badge public"}>
                        {item.isMemberOnly ? "會員" : "一般"}
                      </span>
                    </td>
                    <td>{item.publishedDate}</td>
                    <td>{numberFormat.format(item.views)}</td>
                    <td>{numberFormat.format(item.uniqueVisitors)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">{loading ? "載入中" : "暫無可顯示資料"}</div>
        )}
      </section>
    </div>
  );
}
